import { useQuery } from 'react-query';
import { api } from './useAuth';
import type { TicketUser } from './useTickets';

export interface TicketStatsBundle {
  total: number;
  open: number;
  accepted: number;
  recording: number;
  processing: number;
  completed: number;
  sent: number;
  cancelled: number;
  failed: number;
  pipeline: number;
  needsAttention: number;
  readyToSend: number;
  myOpenQueue: number;
  myActive: number;
  thisWeek: number;
  thisMonth: number;
}

export interface RecentTicket {
  id: string;
  ticketNumber?: string | null;
  title: string;
  status: string;
  branchId?: number | null;
  roomId?: number | null;
  slotStartAt?: string | null;
  sessionId?: string | null;
  createdAt: string;
  updatedAt: string;
  requestedBy?: TicketUser | null;
  assignedTo?: TicketUser | null;
}

export interface RecordingStatsBundle {
  sessionCount: number;
  videoClipCount: number;
  totalDurationSeconds: number;
  averageDurationSeconds: number;
  completedSessions: number;
  inProgressSessions: number;
  plannedSessions: number;
  thisWeekSessions: number;
  thisMonthSessions: number;
}

export interface DashboardStats {
  role?: string;
  tickets: TicketStatsBundle;
  recording?: RecordingStatsBundle;
  recentTickets: RecentTicket[];
  activeTickets: RecentTicket[];
}

export interface AdminDashboardStats extends DashboardStats {
  totalUsers: number;
  videoRecorders: number;
  byRecorder: Array<{
    userId: string;
    userEmail?: string;
    userName?: string;
    total: number;
    completed: number;
    active: number;
  }>;
}

const EMPTY_TICKETS: TicketStatsBundle = {
  total: 0,
  open: 0,
  accepted: 0,
  recording: 0,
  processing: 0,
  completed: 0,
  sent: 0,
  cancelled: 0,
  failed: 0,
  pipeline: 0,
  needsAttention: 0,
  readyToSend: 0,
  myOpenQueue: 0,
  myActive: 0,
  thisWeek: 0,
  thisMonth: 0,
};

function normalizeStatsPayload(data: Record<string, unknown>): DashboardStats | AdminDashboardStats {
  const rawTickets = (data.tickets as Record<string, number> | undefined) ?? {};
  const tickets: TicketStatsBundle = {
    total: rawTickets.total ?? 0,
    open: rawTickets.open ?? 0,
    accepted: rawTickets.accepted ?? 0,
    recording: rawTickets.recording ?? 0,
    processing: rawTickets.processing ?? 0,
    completed: rawTickets.completed ?? 0,
    sent: rawTickets.sent ?? 0,
    cancelled: rawTickets.cancelled ?? 0,
    failed: rawTickets.failed ?? 0,
    pipeline: rawTickets.pipeline ?? 0,
    needsAttention: rawTickets.needsAttention ?? 0,
    readyToSend: rawTickets.readyToSend ?? 0,
    myOpenQueue: rawTickets.myOpenQueue ?? 0,
    myActive: rawTickets.myActive ?? 0,
    thisWeek: rawTickets.thisWeek ?? 0,
    thisMonth: rawTickets.thisMonth ?? 0,
  };

  const rawRecording = data.recording as Record<string, number> | undefined;
  const recording: RecordingStatsBundle | undefined = rawRecording
    ? {
        sessionCount: rawRecording.sessionCount ?? 0,
        videoClipCount: rawRecording.videoClipCount ?? 0,
        totalDurationSeconds: rawRecording.totalDurationSeconds ?? 0,
        averageDurationSeconds: rawRecording.averageDurationSeconds ?? 0,
        completedSessions: rawRecording.completedSessions ?? 0,
        inProgressSessions: rawRecording.inProgressSessions ?? 0,
        plannedSessions: rawRecording.plannedSessions ?? 0,
        thisWeekSessions: rawRecording.thisWeekSessions ?? 0,
        thisMonthSessions: rawRecording.thisMonthSessions ?? 0,
      }
    : undefined;

  const base: DashboardStats = {
    role: data.role as string | undefined,
    tickets,
    recording,
    recentTickets: (data.recentTickets as RecentTicket[]) ?? [],
    activeTickets: (data.activeTickets as RecentTicket[]) ?? [],
  };

  if (data.totalUsers != null) {
    return {
      ...base,
      totalUsers: (data.totalUsers as number) ?? 0,
      videoRecorders: (data.videoRecorders as number) ?? 0,
      byRecorder: (data.byRecorder as AdminDashboardStats['byRecorder']) ?? [],
    };
  }

  return base;
}

export const useUserStats = (scope?: 'user' | 'all') => {
  return useQuery<DashboardStats | AdminDashboardStats>(
    ['user-stats', scope],
    async () => {
      try {
        const qs = scope ? `?scope=${scope}` : '';
        const response = await api.get(`/user-stats${qs}`);
        return normalizeStatsPayload(response.data ?? {});
      } catch {
        return {
          tickets: EMPTY_TICKETS,
          recentTickets: [],
          activeTickets: [],
        };
      }
    },
    {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 2 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      refetchOnMount: true,
    }
  );
};

/** @deprecated Use stats.recentTickets from useUserStats instead */
export const useRecentVideos = (limit: number = 10, scope?: 'user' | 'all') => {
  return useQuery<RecentTicket[]>(
    ['recent-tickets', limit, scope],
    async () => {
      const scopeQs = scope ? `&scope=${scope}` : '';
      const response = await api.get(`/user-stats/recent?limit=${limit}${scopeQs}`);
      return response.data ?? [];
    },
    {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 2 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    }
  );
};

export const useRecentTickets = useRecentVideos;
